/** @format */

/* eslint @typescript-eslint/prefer-nullish-coalescing: "warn", require-await: "warn" */

/* eslint-disable max-lines */
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { omit } from 'lodash';
import { v4 as uuid } from 'uuid';

import { MarketCode } from '@toucan/config/markets';

import { SubmissionErrorCode } from '@toucan/types';
import { CustomerDetailsFormValues } from '@toucan/types/customer';
import { Checkout } from '@toucan/types/mpbx/checkout';

import { client } from '@toucan/services/request-client';
import {
    getCsrfTokenValue,
    rejectIfInvalidCSRFResponse,
} from '@toucan/services/requests/csrf';
import {
    CheckoutMetricLabels,
    checkoutFinalizeAttemptedMetric,
    checkoutFinalizeFailedMetric,
} from '@toucan/services/requests/metrics';
import { getAnonymousToken } from '@toucan/services/requests/user';

import { getPaymentMethodFromCheckoutType } from '@toucan/helpers/checkout';
import { centToDecimal } from '@toucan/helpers/currency';
import logger from '@toucan/helpers/logger';
import { productConditionStringToEnum } from '@toucan/helpers/products';
import { retry } from '@toucan/helpers/retry';
import { isTransactionOfVariantType } from '@toucan/helpers/transaction';
import { isAxiosResponse } from '@toucan/helpers/type-guards';

import { trackCheckoutFinalizeError } from '@toucan/components/organisms/checkout/helpers/analytics';

import { transformOutboundAddressData } from '@toucan/client/core/address/helpers/address';
import {
    CheckoutInBoundPaymentFormValues,
    CheckoutOutBoundPaymentFormValues,
    CheckoutType,
    PaymentGatewayType,
    PaymentOrOutbound,
    isInboundPaymentFormValues,
} from '@toucan/client/core/checkout';
import {
    submitShippingAddress,
    submitShippingOption,
} from '@toucan/client/features/checkout';
import { CalcServiceResponse } from '@toucan/client/global/calculation-service';
import {
    Address,
    DeliverySummary,
    DeliveryType,
    DisputeItem,
    LineItemState,
    MixedTransaction,
    ProductItemSummary,
    PublicTransactionStage,
    TransactionKind,
    TransactionState,
    TransactionSummary,
    TransactionSummaryAddress,
    TransactionVariant,
} from '@toucan/client/global/transaction';

export const transactionTemplate: TransactionSummary = {
    kind: TransactionKind.TRANSACTION_SUMMARY,
    id: '',
    market: '',
    reference: '',
    customerDetails: {
        firstName: '',
        lastName: '',
        emailAddress: '',
        phoneNumber: '',
        receiveOffersViaEmail: false,
        receiveOffersViaText: false,
    },
    customerId: -1,
    billingAddress: {
        id: 0,
        addressee: '',
        line1: '',
        line2: '',
        town: '',
        county: '',
        state: '',
        postcode: '',
        zipcode: '',
        country: '',
        deliveryDefault: false,
        billingDefault: false,
        nickname: '',
    },
    state: TransactionState.UNDEFINED,
    stage: PublicTransactionStage.UNDEFINED_STAGE,
    disputes: [],
    itemsToSell: [],
    itemsToBuy: [],
    itemsToReturn: [],
    collections: [],
    shipments: [],
    deliveries: [],
    totals: {
        totalIncTax: 0,
        totalExTax: 0,
        totalTax: 0,
        totalPayments: 0,
        balance: 0,
    },
    promotions: [],
    createdDate: '',
    modifiedDate: '',
    tax: [],
};

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
interface ItemSellingTransform {
    itemsToSell: ProductItemSummary[];
    itemsToReturn: ProductItemSummary[];
}

type ShippingOption =
    | Pick<DeliverySummary, 'deliveryOption' | 'deliveryExpected'>
    | false;

export type AddShippingDetailsResult = {
    totals?: CalcServiceResponse;
};

/**
 * Transforms a transaction summary address into a regular Address
 * by copying over the customerAddressId in place of the ID stored on the
 * transaction and ensuring that zipcode/state are used for US addresses.
 *
 * @param address
 * @return Address
 */
function transformAddress(address: TransactionSummaryAddress): Address {
    const updatedAddress = {
        ...omit(address, 'customerAddressId'),
        id: address.customerAddressId,
    };

    if (address.country === 'US') {
        updatedAddress.state = address.county;
        updatedAddress.zipcode = address.postcode;
        delete updatedAddress.county;
        delete updatedAddress.postcode;
    }

    return updatedAddress;
}

/**
 * Sort the deliveries returned from the transaction API into
 * two buckets; collections and shipments.
 *
 * @param transaction
 */
function transformDeliveries(
    transaction: TransactionSummary
): TransactionSummary {
    const { deliveries, ...newTransaction } = transaction;

    if (!deliveries) {
        return {
            ...transaction,
            collections: [],
            shipments: [],
        };
    }

    // Match delivery type
    function deliveryTypeEquals(deliveryType: DeliveryType) {
        return function (delivery: any) {
            return delivery.deliveryType === deliveryType;
        };
    }

    // Ensure that a delivery option is in the correct shape
    function transformDeliveryData(delivery: any): DeliverySummary {
        const transformedData = {
            ...delivery,
            deliveryOption: parseInt(delivery.deliveryOption),
        };

        if (delivery.address) {
            transformedData.address = transformAddress(
                delivery.address as TransactionSummaryAddress
            );
        }

        return transformedData;
    }

    const collections = deliveries
        .filter(deliveryTypeEquals(DeliveryType.COLLECTION))
        .map(transformDeliveryData);

    const shipments = deliveries
        .filter(deliveryTypeEquals(DeliveryType.SHIPMENT))
        .map(transformDeliveryData);

    return {
        ...newTransaction,
        deliveries,
        collections,
        shipments,
    };
}

/**
 * Partition items to sell and items to return based on the item state
 * @param items
 */
function partitionItemsSelling(
    items: ProductItemSummary[]
): ItemSellingTransform {
    return items.reduce(
        (accumulator: ItemSellingTransform, item: ProductItemSummary) => {
            const key =
                item.state === LineItemState.RETURNING
                    ? 'itemsToReturn'
                    : 'itemsToSell';
            accumulator[key].push(item);
            return accumulator;
        },
        { itemsToSell: [], itemsToReturn: [] }
    );
}

export const transformCustomer = (
    customer: any
): { customerDetails: CustomerDetails } => {
    if (!customer) {
        // This is to replicate the pre-existing behaviour
        // without getting upset about types
        return { customerDetails: customer };
    }

    return {
        customerDetails: {
            ...customer,
            // This probably ought to throw if the api data is malformed.
            // Optional chain to preserve legacy behaviour until we have more
            // robust fixtures/testing in place for apis.
            emailAddress: customer?.emailAddress?.trim(),
        },
    };
};

/**
 * Transforms the transaction data into a TransactionSummary
 *
 * @param data The transaction summary data from `/transaction-service/${hash}/summary/`
 */
export function transformTransactionSummary(
    data: any,
    itemsToSellStatesToExclude: LineItemState[] = [
        LineItemState.CANCELLED,
        LineItemState.DECLINED,
    ]
): TransactionSummary {
    const transactionSummary: TransactionSummary = {
        ...transactionTemplate,
        ...transformDeliveries(data),
        ...partitionItemsSelling(data.itemsToSell),
        ...transformCustomer(data.customer),
    };

    if (data.billingAddress) {
        transactionSummary.billingAddress = transformAddress(
            data.billingAddress as TransactionSummaryAddress
        );
    }

    // Updates the ProductItemSummary response from the api to use an enum for the condition
    const updateItemConditionsToEnum = (item: any) => {
        return {
            ...item,
            condition: productConditionStringToEnum(item.condition),
        };
    };

    const removeCancelledItems = (item: ProductItemSummary | DisputeItem) => {
        return item.state !== LineItemState.CANCELLED;
    };

    const filterSellItems = (item: ProductItemSummary) => {
        return !itemsToSellStatesToExclude.includes(item.state);
    };

    transactionSummary.itemsToBuy = transactionSummary.itemsToBuy
        .filter(removeCancelledItems)
        .map(updateItemConditionsToEnum);

    transactionSummary.itemsToSell = transactionSummary.itemsToSell
        .filter(filterSellItems)
        .map(updateItemConditionsToEnum);

    transactionSummary.itemsToReturn = transactionSummary.itemsToReturn.map(
        updateItemConditionsToEnum
    );

    transactionSummary.disputes =
        transactionSummary.disputes.filter(removeCancelledItems);

    if (transactionSummary.initialQuote) {
        transactionSummary.initialQuote.items =
            transactionSummary.initialQuote.items.map(
                updateItemConditionsToEnum
            );
    }

    if (transactionSummary.quote) {
        transactionSummary.quote.items = transactionSummary.quote.items.map(
            (item) => {
                const lineItems = [
                    ...transactionSummary.itemsToSell,
                    ...transactionSummary.itemsToReturn,
                ];
                const lineItem = lineItems.find(
                    (lineItem) => lineItem.id === item.lineItemId
                );

                return {
                    ...item,
                    lineItemState:
                        lineItem?.state ?? LineItemState.UNDEFINED_STATE,
                    condition: productConditionStringToEnum(item.condition),
                };
            }
        );
    }

    return transactionSummary;
}

/**
 * Fetch the transaction summary for the given hash.
 *
 * @param hash the transaction hash
 */
export async function getTransactionSummary(
    hash: string
): Promise<TransactionSummary> {
    const { data } = await client.get(`/transaction-service/${hash}/summary/`);
    return transformTransactionSummary(data);
}

/**
 * Fetch a checkout from a given checkout id
 *
 * @param {string} checkoutId The id of the checkout
 * @returns The checkout data
 */
export async function getCheckout(
    checkoutId: string,
    market: MarketCode
): Promise<Checkout> {
    const config: AxiosRequestConfig = {
        headers: {
            'Content-Type': 'application/json',
            'X-Market': market.toLowerCase(),
        },
    };
    const response = await client.get(`/checkout/${checkoutId}/`, config);

    return response.data;
}

export function handleCheckoutCreateResponse(response: AxiosResponse) {
    const { checkoutId, code } = response.data;

    if (code === SubmissionErrorCode.INVALID_CSRF) {
        return Promise.reject(response);
    }

    if (!checkoutId) {
        logger.addLabels({ data: response.data });
        throw 'No Checkout ID';
    }

    return checkoutId;
}

/**
 * Create a checkout from a transaction id.
 *
 * @param transactionId the transaction id
 * @param customerDetails the customer details
 * @param market the market
 *
 * @returns the checkout id used in all other checkout submits
 */
async function createCheckoutFromTransaction(
    transactionId: string,
    customerDetails: CustomerDetails,
    market: MarketCode,
    productIds: number[] = [],
    csrfToken: string
): Promise<string> {
    const config: AxiosRequestConfig = {
        headers: {
            'Content-Type': 'application/json',
            'X-Market': market.toLowerCase(),
            'CSRF-Token': csrfToken,
        },
    };
    const response = await client.post(
        '/checkout/transaction/',
        {
            transaction_id: transactionId,
            phone_number: customerDetails.phoneNumber,
            receive_offers_via_text: customerDetails.receiveOffersViaText,
            receive_offers_via_email: customerDetails.receiveOffersViaEmail,
            goods_out_ids: productIds,
        },
        config
    );

    return handleCheckoutCreateResponse(response);
}

/**
 * Create a checkout from a basket transaction
 */
export async function createCheckoutFromBasket(
    customerDetails: CustomerDetails,
    market: MarketCode,
    csrfToken: string,
    clickRef?: string,
    productIds?: number[]
): Promise<string> {
    const config: AxiosRequestConfig = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Market': market,
            'CSRF-Token': csrfToken,
        },
    };

    const {
        emailAddress,
        phoneNumber,
        firstName,
        lastName,
        receiveOffersViaEmail,
        receiveOffersViaText,
    } = customerDetails;

    const postData: {
        firstName: string;
        lastName: string;
        phoneNumber: string;
        receiveOffersViaEmail: boolean;
        receiveOffersViaText: boolean;
        emailAddress: string;
        receiveEmailMarketing: boolean;
        clickRef?: string;
        product_ids?: number[];
    } = {
        firstName,
        lastName,
        phoneNumber,
        receiveOffersViaEmail,
        receiveOffersViaText,
        emailAddress,
        receiveEmailMarketing: receiveOffersViaEmail,
        ...(clickRef && { clickRef }),
    };

    if (productIds && productIds.length) {
        postData.product_ids = productIds;
    }

    const response = await client.post('/checkout/basket/', postData, config);

    return handleCheckoutCreateResponse(response);
}

export async function createCheckout(
    transactionId: string,
    customerDetails: CustomerDetails,
    market: MarketCode,
    productIds: number[],
    csrfToken: string,
    clickRef?: string
): Promise<string> {
    const checkoutId =
        transactionId === ''
            ? await createCheckoutFromBasket(
                  customerDetails,
                  market,
                  csrfToken,
                  clickRef,
                  productIds
              )
            : await createCheckoutFromTransaction(
                  transactionId,
                  customerDetails,
                  market,
                  productIds,
                  csrfToken
              );

    return checkoutId;
}

export async function addBillingAddress(
    checkoutId: string,
    billingAddress: Address,
    market: MarketCode,
    csrfToken: string
): Promise<number> {
    const response = await submitBillingAddress(
        checkoutId,
        billingAddress,
        market,
        csrfToken
    );
    return response.data.addressId;
}

async function submitBillingAddress(
    checkoutId: string,
    billingAddress: Address,
    market: MarketCode,
    csrfToken: string
): Promise<AxiosResponse> {
    const config: AxiosRequestConfig = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Market': market.toLowerCase(),
            'CSRF-Token': csrfToken,
        },
    };

    const response = await client.post(
        `/checkout/${checkoutId}/add-billing-address/`,
        transformOutboundAddressData(billingAddress),
        config
    );

    return rejectIfInvalidCSRFResponse(response);
}

export async function addShippingDetails(
    checkoutId: string,
    shippingAddress: Address,
    shippingOption: ShippingOption,
    market: MarketCode,
    csrfToken: string
): Promise<AddShippingDetailsResult> {
    await submitShippingAddress(checkoutId, shippingAddress, market, csrfToken);

    if (!shippingOption) {
        return {};
    }

    const { deliveryOption, deliveryExpected } = shippingOption;

    const response = await submitShippingOption(
        checkoutId,
        deliveryOption,
        deliveryExpected,
        market,
        csrfToken
    );

    return {
        totals: response.data.totals,
    };
}
export function addPaymentDetails(
    checkoutId: string,
    paymentDetails: PaymentOrOutbound,
    paymentAmount: number,
    market: MarketCode,
    csrfToken: string
): Promise<AxiosResponse<Checkout>> | null {
    if (paymentDetails === false && paymentAmount === 0) {
        return submitZeroBalanceTransaction(checkoutId, market, csrfToken);
    }

    if (isInboundPaymentFormValues(paymentDetails)) {
        return submitInboundPayment(
            checkoutId,
            paymentDetails,
            paymentAmount,
            market,
            csrfToken
        );
    }

    if (paymentDetails) {
        return submitOutboundPayment(
            checkoutId,
            paymentDetails,
            market,
            csrfToken
        );
    }
    return null;
}

/**
 * submit a promotional code
 *
 * @param checkoutId the checkout id
 * @param code the promotional code
 * @param market {MarketCode} the market code
 */
export async function submitPromoCode(
    checkoutId: string,
    code: string,
    market: MarketCode,
    csrfToken: string
): Promise<AxiosResponse<Checkout>> {
    const config: AxiosRequestConfig = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Market': market.toLowerCase(),
            'CSRF-Token': csrfToken,
        },
    };

    const checkout = await client.post(
        `/checkout/${checkoutId}/promotion-code/`,
        {
            code,
        },
        config
    );

    return rejectIfInvalidCSRFResponse(checkout);
}

/**
 * Submit payment details for an inbound payment.
 *
 * @param checkoutId the checkout id
 * @param cardPayment the card payment data
 * @param paymentAmount the payment amount
 * @param market {MarketCode} the market code
 */
async function submitInboundPayment(
    checkoutId: string,
    {
        token,
        deviceData,
        paymentGateway,
        checkoutType,
    }: CheckoutInBoundPaymentFormValues,
    paymentAmount: number,
    market: MarketCode,
    csrfToken: string
): Promise<AxiosResponse<Checkout>> {
    const config: AxiosRequestConfig = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Market': market.toLowerCase(),
            'CSRF-Token': csrfToken,
        },
    };

    const paymentMethod = getPaymentMethodFromCheckoutType(checkoutType);

    const checkout = await client.post(
        `/checkout/${checkoutId}/payment-gateway/`,
        {
            payment_amount: centToDecimal(paymentAmount),
            token,
            gateway: paymentGateway,
            method: paymentMethod,
            device_data: deviceData,
        },
        config
    );

    return rejectIfInvalidCSRFResponse(checkout);
}

/**
 * Submit payment details for an outbound balance.
 *
 * @param checkoutId the checkout id
 * @param details the account details to send money to
 * @param market {MarketCode} the market code
 */
async function submitOutboundPayment(
    checkoutId: string,
    details: Partial<CheckoutOutBoundPaymentFormValues>,
    market: MarketCode,
    csrfToken: string
): Promise<AxiosResponse<Checkout>> {
    const config: AxiosRequestConfig = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Market': market.toLowerCase(),
            'CSRF-Token': csrfToken,
        },
    };

    const dataToSend: any = {};

    if (details.accountNumber) {
        dataToSend.payment_account_number = details.accountNumber;
        dataToSend.payment_sort_code = details.sortCode;
    } else if (details.iban) {
        dataToSend.payment_iban = details.iban;
        dataToSend.payment_swift = details.swift;
    } else if (details.usAccountNumber) {
        dataToSend.payment_us_account_number = details.usAccountNumber;
        dataToSend.payment_routing_number = details.routingNumber;
    }

    const checkout = await client.post(
        `/checkout/${checkoutId}/outbound-payment/`,
        dataToSend,
        config
    );

    return rejectIfInvalidCSRFResponse(checkout);
}

/**
 * Adds payment details for a zero balance transaction
 *
 * @param checkoutId The checkout id
 * @param market {MarketCode} the market code
 */
async function submitZeroBalanceTransaction(
    checkoutId: string,
    market: MarketCode,
    csrfToken: string
): Promise<AxiosResponse<Checkout>> {
    const config = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Market': market.toLowerCase(),
            'CSRF-Token': csrfToken,
        },
    };

    const checkout = await client.post(
        `/public-api/checkout/v1/${checkoutId}/zero-balance-transaction/`,
        undefined,
        config
    );

    return rejectIfInvalidCSRFResponse(checkout);
}

export async function finalizeCheckout(
    checkoutId: string,
    market: MarketCode,
    csrfToken: string
): Promise<AxiosResponse<Checkout>> {
    const csrf = csrfToken ? csrfToken : await getCsrfTokenValue();

    const config = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Market': market.toLowerCase(),
            'CSRF-Token': csrf,
        },
    };

    const checkout = await client.post(
        `/checkout/${checkoutId}/finalize/`,
        undefined,
        config
    );

    return rejectIfInvalidCSRFResponse(checkout);
}

export async function finalizeCheckoutWithRetryAndMetrics(
    checkoutId: string,
    market: MarketCode,
    csrfToken: string,
    paymentGateway: PaymentGatewayType,
    checkoutType: CheckoutType,
    hasRetried: boolean = false
): Promise<ReturnType<typeof finalizeCheckout>> {
    const metricLabels: CheckoutMetricLabels = {
        market,
        gateway: paymentGateway,
        checkoutType,
    };

    try {
        checkoutFinalizeAttemptedMetric.inc(metricLabels);
        return await finalizeCheckout(checkoutId, market, csrfToken);
    } catch (error: any) {
        if (error?.response && error?.response.status === 401 && !hasRetried) {
            logger.addLabels({ data: error });
            logger.error(
                new Error(
                    `Checkout Finalize Error (retry required): [market: ${market}, error: ${error.message}]`
                )
            );
            await getAnonymousToken(market);
            return await finalizeCheckoutWithRetryAndMetrics(
                checkoutId,
                market,
                csrfToken,
                paymentGateway,
                checkoutType,
                true
            );
        }
        const errorId = uuid();
        const data = error?.message?.response?.data ?? error;

        trackCheckoutFinalizeError({
            checkoutId,
            type: checkoutType,
            errorId,
        });
        checkoutFinalizeFailedMetric.inc(metricLabels);
        logger.addLabels({ data, errorId });
        logger.error(
            new Error(
                `Checkout Finalize Error: [market: ${market}, error: ${error.message}]`
            )
        );
        throw error;
    }
}

export async function pollFinalizeCheckoutWithRetryAndMetrics(
    checkoutId: string,
    market: MarketCode,
    csrfToken: string,
    paymentGateway: PaymentGatewayType,
    checkoutType: CheckoutType
) {
    const pollResponse = await retry(async () => {
        const finalizeResponse = await (async () => {
            try {
                return await finalizeCheckoutWithRetryAndMetrics(
                    checkoutId,
                    market,
                    csrfToken,
                    paymentGateway,
                    checkoutType
                );
            } catch (error) {
                // Swallow the error as we do not want the retry function
                // to try again when the request fails
                return error;
            }
        })();

        if (
            isAxiosResponse(finalizeResponse) &&
            finalizeResponse.status === 202
        ) {
            // Throw error here to cause the retry function to try again
            throw new Error();
        }

        return finalizeResponse;
    });

    if (pollResponse instanceof Error || axios.isAxiosError(pollResponse)) {
        return Promise.reject(pollResponse);
    }

    return pollResponse;
}

type CustomerDetails = Pick<
    CustomerDetailsFormValues,
    | 'receiveOffersViaEmail'
    | 'receiveOffersViaText'
    | 'phoneNumber'
    | 'firstName'
    | 'lastName'
    | 'emailAddress'
>;

/**
 * Get the parent transaction based on the current transaction relationship
 */
export async function getParentTransaction(
    transaction: MixedTransaction
): Promise<TransactionSummary | undefined> {
    const transactionSummary = transaction as TransactionSummary;

    const hasRelationships = transactionSummary?.relationship;

    const isReturnTransaction = isTransactionOfVariantType(
        transaction,
        TransactionVariant.RETURN
    );
    const isPartRefund = isTransactionOfVariantType(
        transaction,
        TransactionVariant.PART_REFUND
    );

    const isChildTransaction = isReturnTransaction || isPartRefund;

    if (!hasRelationships || !isChildTransaction) {
        return;
    }

    const parentId = transactionSummary?.relationship?.parent?.id;

    if (parentId) {
        const transaction = await getTransactionSummary(parentId);
        return transaction;
    }

    return;
}
